import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import {
    CalendarDays,
    Clock,
    Gift,
    MapPin,
    Navigation,
    Shirt,
    TrainFront
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { eventLocation, googleMapsUrl } from '@/lib/calendar'
import CopyAddressButton from '@/components/landing/copy-address-button'
import SectionHeading from '@/components/landing/section-heading'
import { invitation } from '@/constants/invitation'

const DetailsSection: FC = (): ReactNode => {
    const { date, time, venue, access, dressCode, gifts } = invitation

    const facts = [
        { icon: CalendarDays, label: "Date", value: date.full },
        { icon: Clock, label: "Horaires", value: `De ${time.start} à ${time.end}` }
    ]

    const cards = [
        { icon: Shirt, label: "Dress code", ...dressCode, tile: 'bg-festive-saffron text-foreground' },
        { icon: TrainFront, label: "Accès", ...access, tile: 'bg-festive-teal text-foreground' },
        { icon: Gift, label: "Cadeaux", ...gifts, tile: 'bg-festive-raspberry text-primary-foreground' }
    ]

    return (
        <section id='infos' aria-labelledby='infos-titre' className='py-24 sm:py-32'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    id='infos-titre'
                    eyebrow="Infos pratiques"
                    title="Tout ce qu’il faut savoir"
                    description="Le lieu, l’accès et quelques petits détails pour arriver serein·e le jour J."
                />

                <div className='mt-14 grid gap-6 lg:grid-cols-[1.25fr_1fr]'>
                    <article className='overflow-hidden rounded-[2rem] bg-card shadow-xl shadow-foreground/5 ring-1 ring-foreground/5'>
                        <div className='relative aspect-[16/9]'>
                            <Image
                                src='/images/lieu.jpg'
                                alt='Une salle décorée de guirlandes lumineuses sur un mur rose poudré'
                                fill
                                sizes='(min-width: 1024px) 38rem, (min-width: 640px) 90vw, 100vw'
                                className='object-cover'
                            />

                            <span className='absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-card/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wider shadow-sm backdrop-blur'>
                                <MapPin className='size-3.5 text-primary' aria-hidden='true' />
                                Le lieu
                            </span>
                        </div>

                        <div className='p-7 sm:p-9'>
                            <h3 className='font-heading text-3xl tracking-tight sm:text-4xl'>
                                {venue.name}
                            </h3>

                            <address className='mt-2 text-lg not-italic text-muted-foreground'>
                                {venue.street}, {venue.postalCode} {venue.city}
                            </address>

                            <p className='mt-5 leading-relaxed text-pretty'>
                                {venue.description}
                            </p>

                            <ul className='mt-6 grid gap-3 sm:grid-cols-2'>
                                {facts.map(({ icon: Icon, label, value }) => (
                                    <li key={label} className='flex items-start gap-3 rounded-2xl bg-muted px-4 py-3'>
                                        <Icon className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />

                                        <div>
                                            <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                                {label}
                                            </p>

                                            <p className='font-semibold'>
                                                {value}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div className='mt-7 flex flex-col gap-3 sm:flex-row'>
                                <a
                                    href={googleMapsUrl()}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={buttonStyles()}
                                >
                                    <Navigation aria-hidden='true' />
                                    Voir l’itinéraire
                                    <span className='sr-only'>(s’ouvre dans un nouvel onglet)</span>
                                </a>

                                <CopyAddressButton address={eventLocation} />
                            </div>
                        </div>
                    </article>

                    <div className='grid gap-6'>
                        {cards.map(({ icon: Icon, label, title, text, tile }) => (
                            <article
                                key={label}
                                className='flex gap-5 rounded-[2rem] bg-card p-7 shadow-sm ring-1 ring-foreground/5'
                            >
                                <span className={cn('grid size-12 shrink-0 place-items-center rounded-2xl', tile)}>
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>

                                <div>
                                    <p className='text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground'>
                                        {label}
                                    </p>

                                    <h3 className='mt-1.5 text-xl font-bold'>
                                        {title}
                                    </h3>

                                    <p className='mt-2 leading-relaxed text-muted-foreground text-pretty'>
                                        {text}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default DetailsSection
